
































































































































import { Vue, Component, Ref } from 'vue-property-decorator'
import { parse } from 'papaparse'
import moment from 'moment'
import ListView from '@/components/ListView.vue'
import DateSelect from '@/components/DateSelect.vue'
import ShopAutocomplete from '@/components/ShopAutocomplete.vue'
import CSVExportBtn from '@/components/CSVExport.vue'
import { Shop } from '@/types/shop'
import { formatDateForDisplay } from '@/utils'
import { Holiday } from '@/types'

const parseCSV = (file: File) => {
  return new Promise((resolve, reject) => {
    parse(file, {
      error: err => {
        reject(err)
      },
      complete: results => {
        return resolve(results.data)
      },
    })
  })
}

const transformCSV = (data: string[][], shops: Shop[]) => {
  const header = data[0]
  if (!header) return []
  const shopCodeIdx = header.findIndex(x => String(x).match('コード'))
  const dateIdx = header.findIndex(x => String(x).match('日'))
  const memoIdx = header.findIndex(x => String(x).match('メモ'))
  const code2id: Map<string, Shop> = new Map()
  shops.forEach(shop => {
    code2id.set(shop.code, shop)
  })
  data = data.slice(1)
  return data.map(row => {
    const shop = code2id.get(row[shopCodeIdx])
    const date = moment(row[dateIdx])
    return {
      isValid: Boolean(shop) && date.isValid(),
      shop: shop?.id,
      shopCode: row[shopCodeIdx],
      shopName: shop?.name,
      date: date.isValid() ? date.format('YYYY-MM-DD') : '',
      memo: row[memoIdx] || '',
    }
  })
}

@Component({
  components: { ListView, DateSelect, ShopAutocomplete, CSVExportBtn },
})
export default class HolidayView extends Vue {
  @Ref() readonly listView!: ListView

  filterDefinition = {
    ordering: { type: String, default: '' },
    shop: { type: String, default: null },
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null },
  }
  filter: null | Record<string, any> = null
  selected: number[] = []

  get headers() {
    return [
      { text: '', value: 'check', width: 50 },
      { text: this.$t('holiday.text.shopCode'), value: 'shopCode', width: 60 },
      { text: this.$t('holiday.text.shopName'), value: 'shopName', width: 200 },
      { text: this.$t('holiday.text.holidayDate'), value: 'date', width: 100 },
      { text: this.$t('holiday.text.memo'), value: 'memo' },
      {
        text: this.$t('holiday.text.registeredDateTime'),
        value: 'createdAt',
        width: 150,
      },
    ]
  }
  get csvUploadHeaders() {
    return [
      { text: this.$t('holiday.text.shopCode'), value: 'shopCode' },
      { text: this.$t('holiday.text.shopName'), value: 'shopName' },
      { text: this.$t('holiday.text.shopId'), value: 'shop' },
      {
        text: this.$t('holiday.text.holidayDate'),
        value: (item: Holiday) =>
          formatDateForDisplay(item.date, this.$i18n.locale),
      },
      { text: this.$t('holiday.text.memo'), value: 'memo' },
    ]
  }
  shops: Shop[] = []
  bulkData: {
    isValid: boolean
    shopCode: any
    shopName: any
    shop: any
    date: string
    memo: string
  }[] = []
  isUploading = false
  dialog = false
  get csvHeaders() {
    const t = this.$t.bind(this)
    return [
      { key: 'id', label: t('holiday.csv.id') },
      { key: 'shopCode', label: t('holiday.csv.shopCode') },
      { key: 'shopName', label: t('holiday.csv.shopName') },
      { key: 'date', label: t('holiday.csv.holidayDate') },
      { key: 'memo', label: t('holiday.csv.memo') },
    ]
  }
  created() {
    this.fetchShop()
  }
  updateQuery() {
    if (this.filter !== null) this.listView.updateQuery(this.filter)
    this.selected = []
  }
  async onDelete() {
    if (!confirm(String(this.$t('common.message.confirmDelete')))) return
    const api = this.$api.holidays
    try {
      await Promise.all(this.selected.map(id => api(id).delete()))
      this.selected = []
    } catch (err) {
      console.error(err)
      this.$toast.error(String(this.$t('toast.message.deleteError')))
    }
    this.listView.reload()
  }
  onSelectedChange(checked: boolean, id: number) {
    const selected = this.selected
    const idx = selected.findIndex(x => x === id)
    if (idx >= 0) {
      if (!checked) selected.splice(idx, 1)
    } else {
      if (checked) selected.push(id)
    }
  }
  async fetchShop(nextUrl?: string) {
    const { results, next } = await (nextUrl
      ? this.$api.http.get(nextUrl)
      : this.$api.shops().list())
    this.shops = this.shops.concat(results)
    if (next) this.fetchShop(next)
  }

  async onFileChanged(file: null | File) {
    console.log('onFileChanged', file)
    if (file) {
      const csvData = (await parseCSV(file)) as any[][]
      this.bulkData = transformCSV(csvData, this.shops)
    }
  }
  async upload() {
    this.isUploading = true
    try {
      await this.$api
        .holidays()
        .bulkUpload(this.bulkData.filter(x => x.isValid))
      this.bulkData = []
      this.$toast.success(String(this.$t('toast.message.uploadSuccess')))
      this.dialog = false
    } catch (err) {
      console.error(err)
      this.$toast.error(String(this.$t('toast.message.uploadFailed')))
    }
    this.isUploading = false
  }

  downloadTemplate() {
    const filename = 'template.csv'
    let rows = [
      [
        String(this.$t('holiday.text.shopCode')),
        String(this.$t('holiday.text.holidayDate')),
        String(this.$t('holiday.text.memo')),
      ],
      ['101', '2021-05-01', String(this.$t('holiday.text.temporaryHoliday'))],
    ]

    let content = rows.map(row => row.join(',')).join('\r\n')
    let bom = new Uint8Array([0xef, 0xbb, 0xbf])
    let blob = new Blob([bom, content], { type: 'text/csv' })

    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, filename)
    } else {
      let a = document.createElement('a')
      a.download = filename
      // a.target = '_blank'
      a.href = window.URL.createObjectURL(blob)

      // firefox対策． chromeの場合は append/remove をしなくていいらしい
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }
}
